import {APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, InjectionToken, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';

import {CoreRoutingModule} from './core-routing.module';

import {ModalModule} from 'angular2-modal';
import {BootstrapModalModule} from 'angular2-modal/plugins/bootstrap';
//  Modules
import {SharedModule} from '../shared/shared.module';
//  Components
import {DashboardComponent} from './components/dashboard/dashboard.component';
import {UnsubscribeUserComponent} from './components/unsubscribe-user/unsubscribe-user.component';
import {NotFoundComponent} from './components/not-found/not-found.component';
import {InboxComponent} from './components/inbox/inbox.component';
//  Services
import {TranslateService} from './services/translate.service';
import {SmartModalService} from './services/smart-modal.service';
import {ConfigService} from './services/config.service';
import {StorageService} from './services/storage.service';
import {AuthService} from './services/auth.service';
import {RequestService} from './services/request.service';
import {SessionService} from './services/session.service';
import {NotificationService} from './services/notification.service';
import {ModalService} from './services/modal.service';
import {UtilService} from './services/util.service';
import {AuthGuard} from './services/auth-guard.service';
import {NonAuthGuard} from './services/non-auth-guard.service';

import {Guard} from './services/guard.service';
import {PermissionsService} from './services/permissions.service';

import {MiddlemanService} from './services/middleman.service';
import {ProfileService} from './components/profile/profile.service';
import {SmartLoaderService} from './services/smart-loader.service';
import {AlertService} from './services/alert.service';
import {InboxService} from './components/inbox/inbox.service';
import {GdprModalMessageService} from './components/gdpr-modal-message/gdpr-modal-message.service';
import {SocketService} from './services/socket.service';
//  Partials
import {NavbarComponent} from './components/partials/navbar/navbar.component';
import {FooterComponent} from './components/partials/footer/footer.component';
import {ProgressBarComponent} from './components/partials/progress-bar/progress-bar.component';
import {AlertComponent} from './components/partials/alert/alert.component';
import {GdprModalMessageComponent} from './components/gdpr-modal-message/gdpr-modal-message.component';
import {AgoraModule} from 'app/agora/agora.module';
import {OrganizationModule} from 'app/organization/organization.module';
import {DynamicOnboardingComponent} from './components/dynamic-onboarding/dynamic-onboarding.component';
import {StepTemplateBaseComponent} from './components/dynamic-onboarding/step-template-base/step-template-base.component';
import {EmployeesCardComponent} from './components/dynamic-onboarding/employees-card/employees-card.component';
import {GdprCardComponent} from './components/dynamic-onboarding/gdpr-card/gdpr-card.component';
import {OnboardingItemComponent} from './components/dynamic-onboarding/onboarding-item/onboarding-item.component';
import {DynamicOnboardingService} from './components/dynamic-onboarding/dynamic-onboarding.service';
import {OverviewCardComponent} from './components/dynamic-onboarding/overview-card/overview-card.component';
import {BrandingCardComponent} from './components/dynamic-onboarding/branding-card/branding-card.component';

import {InboxTabComponent} from './components/inbox/common/components/inbox-tab/inbox-tab.component';
import {LogoCardComponent} from 'app/core/components/dynamic-onboarding/logo/logo.component';
import {WatermarkCardComponent} from 'app/core/components/dynamic-onboarding/watermark/watermark.component';
import {DomainCardComponent} from 'app/core/components/dynamic-onboarding/domain/domain.component';
import {DynamicOnboardingConfigurationService} from './components/dynamic-onboarding/dynamic-onboarding-configuration.service';
import {InstructionsComponent} from './components/dynamic-onboarding/instructions/instructions.component';
import {CompleteProfileComponent} from './components/complete-profile/complete-profile.component';
import {CompleteProfileService} from './components/complete-profile/complete-profile.service';
import {BuyerToSellerWrapperComponent} from './components/buyer-to-seller-wrapper/buyer-to-seller-wrapper.component';
import {BuyerToSellerComponent} from './components/buyer-to-seller-wrapper/buyer-to-seller/buyer-to-seller.component';
import {BenefitsComponent} from './components/buyer-to-seller-wrapper/buyer-to-seller/benefits/benefits.component';
import {SellerTermsAndConditionsComponent} from './components/buyer-to-seller-wrapper/buyer-to-seller/seller-terms-and-conditions/seller-terms-and-conditions.component';
import {BuyerToSellerService} from './components/buyer-to-seller-wrapper/buyer-to-seller/buyer-to-seller.service';
import {LeapestMessageComponent} from './components/buyer-to-seller-wrapper/leapest-message/leapest-message.component';
import {LogoutComponent} from './components/logout/logout.component';
import {LoginPromptComponent} from './components/login-prompt/login-prompt.component';

import {WindowOpenerService} from './services/window-opener.service';
import {AppLoadService} from './services/app-load.service';
import {ConversationDetailsComponent} from './components/inbox/common/components/conversation-details/conversation-details.component';
import {ReportGeneratorsService} from './services/report-generators.service';
import {DomainService} from './services/domain.service';
import {PageNotAvailableComponent} from './components/page-not-available/page-not-available';
import {NonPermissionGuard} from './services/non-permission-guard.service';
import {ZoomAuthLandingComponent} from './components/zoom-auth-landing/zoom-auth-landing.component';
import {ConferenceToolService} from './services/conference-tool.service';
import { EnvironmentService } from './services/environment.service';
import { LoginComponent } from './components/login/login.component';
import { OktaService } from './services/okta.service';
import { OktaGuard } from './services/okta-guard.service';
import { OktaCallbackComponent } from './components/okta-callback/okta-callback.component';


export function appLoadFactory(ds: AppLoadService) {
    return () => {
        return ds.returnPromise().then(() => {
            return ds.initializeDomainConfig()
        });
    };
}


@NgModule({
    imports: [
        FormsModule,
        CommonModule,
        CoreRoutingModule,
        ModalModule.forRoot(),
        BootstrapModalModule,
        SharedModule,
        ReactiveFormsModule,
        AgoraModule,
        OrganizationModule
    ],
    declarations: [
        NavbarComponent,
        FooterComponent,
        ProgressBarComponent,
        DashboardComponent,
        UnsubscribeUserComponent,
        NotFoundComponent,
        AlertComponent,
        InboxComponent,
        GdprModalMessageComponent,
        LogoCardComponent,
        WatermarkCardComponent,
        DomainCardComponent,
        DynamicOnboardingComponent,
        StepTemplateBaseComponent,
        EmployeesCardComponent,
        GdprCardComponent,
        OnboardingItemComponent,
        OverviewCardComponent,
        BrandingCardComponent,
        InboxTabComponent,
        InstructionsComponent,
        CompleteProfileComponent,
        LogoutComponent,
        LoginComponent,
        LoginPromptComponent,
        BuyerToSellerComponent,
        BenefitsComponent,
        SellerTermsAndConditionsComponent,
        BuyerToSellerWrapperComponent,
        LeapestMessageComponent,
        ConversationDetailsComponent,
        PageNotAvailableComponent,
        ZoomAuthLandingComponent,
        OktaCallbackComponent
    ],
    exports: [
        NavbarComponent,
        FooterComponent,
        AlertComponent,
        PageNotAvailableComponent
    ],
    providers: [
        ConfigService,
        StorageService,
        RequestService,
        SessionService,
        NotificationService,
        ModalService,
        UtilService,
        AuthGuard,
        NonAuthGuard,
        SmartModalService,
        ProfileService,
        TranslateService,
        SmartLoaderService,
        Guard,
        AlertService,
        PermissionsService,
        MiddlemanService,
        InboxService,
        GdprModalMessageService,
        SocketService,
        DynamicOnboardingService,
        DynamicOnboardingConfigurationService,
        CompleteProfileService,
        WindowOpenerService,
        AuthService,
        AppLoadService,
        OktaService,
        OktaGuard,
        ReportGeneratorsService,
        {
            provide: APP_INITIALIZER,
            useFactory: appLoadFactory,
            deps: [AppLoadService],
            multi: true
        },
        BuyerToSellerService,
        DomainService,
        ConferenceToolService,
        NonPermissionGuard,
        EnvironmentService
    ],
    entryComponents: [
        GdprModalMessageComponent,
        EmployeesCardComponent,
        DynamicOnboardingComponent,
        GdprCardComponent,
        InstructionsComponent,
        OverviewCardComponent,
        BrandingCardComponent,
        CompleteProfileComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],

})
export class CoreModule { }


// WEBPACK FOOTER //
// ./src/app/core/core.module.ts